import type {
  WhatsWorkingMetric,
  WhatsWorkingResponse,
  WhatsWorkingRow,
  WhatsWorkingWhatIf,
} from '../models/whatsWorking.types'

import {
  decodeContext,
  decodeCreativeIds,
  numberOrNull,
  percentToRatio,
  record,
  stringOrNull,
  valueOrEmpty,
} from './reportContext'

function decodeWhatIf(value: unknown): WhatsWorkingWhatIf | null {
  const source = record(value)
  if (!source) return null
  return {
    benchmark_feature_value: stringOrNull(source.benchmark_feature_value),
    benchmark_metric_value: numberOrNull(source.benchmark_metric_value),
    metric_improvement_pct: percentToRatio(numberOrNull(source.metric_improvement_pct)),
    reason: source.reason === null ? null : valueOrEmpty(source.reason),
  }
}

function decodeRow(value: unknown): WhatsWorkingRow | null {
  const source = record(value)
  if (!source || typeof source.feature_key !== 'string') return null
  const creativeIds = decodeCreativeIds(source.creative_ids)
  return {
    feature_key: source.feature_key,
    feature_value: valueOrEmpty(source.feature_value),
    sample_size: numberOrNull(source.sample_size) ?? 0,
    metric_value: numberOrNull(source.metric_value),
    diff_percent: percentToRatio(numberOrNull(source.diff_percent)),
    what_if: decodeWhatIf(source.what_if),
    channel: decodeContext(source.channel),
    objective: decodeContext(source.objective),
    campaign_type: decodeContext(source.campaign_type),
    ...(creativeIds.length > 0 ? { creative_ids: creativeIds } : {}),
  }
}

function decodeRows(value: unknown): WhatsWorkingRow[] {
  return Array.isArray(value)
    ? value.flatMap((row) => {
        const decoded = decodeRow(row)
        return decoded ? [decoded] : []
      })
    : []
}

function decodeMetric(value: unknown): WhatsWorkingMetric | null {
  const source = record(value)
  if (!source || typeof source.metric !== 'string' || source.metric.length === 0) return null
  return {
    metric: source.metric,
    working: decodeRows(source.working),
    not_working: decodeRows(source.not_working),
  }
}

function dropdownables(value: unknown): Record<string, string[]> {
  const source = record(value)
  if (!source) return {}
  return Object.fromEntries(
    Object.entries(source).flatMap(([field, entry]) => {
      const values = Array.isArray(entry)
        ? entry
        : record(entry)?.values && Array.isArray(record(entry)?.values)
          ? (record(entry)?.values as unknown[])
          : []
      return values.length ? [[field, values.map(String).filter(Boolean)]] : []
    }),
  )
}

/**
 * Backend compatibility boundary for `section=whats_working`.
 * Invalid optional rows are ignored, while a missing metrics array stays a valid empty response.
 */
export function decodeWhatsWorkingResponse(value: unknown): WhatsWorkingResponse {
  const source = record(value)
  const metricRows = Array.isArray(source?.metrics) ? source.metrics : []
  return {
    metrics: metricRows.flatMap((metric) => {
      const decoded = decodeMetric(metric)
      return decoded ? [decoded] : []
    }),
    dropdownables: dropdownables(source?.dropdownable_dimensions ?? source?.dropdownables),
  }
}
